import { Checkbox, Radio, createTheme } from '@mantine/core'
import type { MantineColorsTuple } from '@mantine/core'
import { raundColors } from './colors'
import focusRingStyles from './focusRing.module.css'
import { raundSpacing } from './spacing'

// ──────────────────────────────────────────────────────────
// TypeScript 型拡張 — カスタムカラー名を型安全に利用するため
// ──────────────────────────────────────────────────────────
declare module '@mantine/core' {
  export interface MantineThemeColorsOverride {
    colors: Record<
      | 'primary'
      | 'secondary'
      | 'tertiary'
      | 'neutral'
      | 'success'
      | 'info'
      | 'warning'
      | 'danger'
      | 'system'
      | (string & {}), // 任意の hex 等もエラーにしない
      MantineColorsTuple
    >
  }

  // カスタムスペーシングキー ('2xl' 〜 '5xl') を型安全に利用するための拡張
  export interface MantineSpacingOverride {
    '2xl': string
    '3xl': string
    '4xl': string
    '5xl': string
  }
}

// ──────────────────────────────────────────────────────────
// Mantine 10スロット カラースケール
//
// マッピング方針:
//   [0] Tint (最淡)
//   [1] Light
//   [2] Border
//   [3] 補間
//   [4] 補間
//   [5] Base  ← primaryShade: 5 (デフォルト使用色)
//   [6] Hover
//   [7] Active
//   [8] 補間 (暗め)
//   [9] 最暗
//
// primaryShade: 5 にすることで
//   [5] = Base, [6] = Hover, [7] = Active が
//   Mantine の hover/active 挙動と自然に対応する。
// ──────────────────────────────────────────────────────────

const primaryScale: MantineColorsTuple = [
  raundColors.primary.tint, // [0] #FFFAE6
  raundColors.primary.light, // [1] #FFF4CC
  raundColors.primary.border, // [2] #FFE08A
  '#FFD347', // [3] 補間
  '#FFCA14', // [4] 補間
  raundColors.primary.base, // [5] #FFC500 ← primaryShade
  raundColors.primary.hover, // [6] #E0AC00
  raundColors.primary.active, // [7] #C99700
  '#A07800', // [8] 補間
  '#7A5900', // [9] 最暗
]

const secondaryScale: MantineColorsTuple = [
  raundColors.secondary.tint, // [0] #FFF5E6
  raundColors.secondary.light, // [1] #FFE8CC
  raundColors.secondary.border, // [2] #FFD9A3
  '#FFCC73', // [3] 補間
  '#FFBC3D', // [4] 補間
  raundColors.secondary.base, // [5] #FF8A00 ← primaryShade
  raundColors.secondary.hover, // [6] #E67700
  raundColors.secondary.active, // [7] #CC6900
  '#A35400', // [8] 補間
  '#7A3F00', // [9] 最暗
]

const tertiaryScale: MantineColorsTuple = [
  raundColors.tertiary.tint, // [0] #F2FBF0
  raundColors.tertiary.light, // [1] #E6F6E1
  raundColors.tertiary.border, // [2] #CDEBC3
  '#A7D99A', // [3] 補間
  '#81CC72', // [4] 補間
  raundColors.tertiary.base, // [5] #5BBF3A ← primaryShade
  raundColors.tertiary.hover, // [6] #4DA532
  raundColors.tertiary.active, // [7] #3F8C2A
  '#2E6620', // [8] 補間
  '#1E4215', // [9] 最暗
]

// System (Gray) — Mantine 組み込みの 'gray' を上書きしないよう 'neutral' キーを使用
const neutralScale: MantineColorsTuple = [
  raundColors.system.tint, // [0] #EEEEEE
  raundColors.system.light, // [1] #E0E0E0
  '#C7C7C7', // [2] 補間
  '#ADADAD', // [3] 補間
  '#949494', // [4] 補間
  raundColors.system.base, // [5] #757575 ← primaryShade
  raundColors.system.hover, // [6] #616161
  raundColors.system.active, // [7] #424242
  '#2E2E2E', // [8] 補間
  '#1A1A1A', // [9] 最暗
]

const successScale: MantineColorsTuple = [
  raundColors.success.tint, // [0] #EEF9F3
  raundColors.success.light, // [1] #DDF3E8
  '#AADFC4', // [2] 補間
  '#7CCB9F', // [3] 補間
  '#56B787', // [4] 補間
  raundColors.success.base, // [5] #2FA36B ← primaryShade
  '#268A5A', // [6] 補間
  '#1D7049', // [7] 補間
  '#145737', // [8] 補間
  '#0B3D25', // [9] 最暗
]

const infoScale: MantineColorsTuple = [
  raundColors.info.tint, // [0] #F0F6FC
  raundColors.info.light, // [1] #E1EEF8
  '#B6D4EF', // [2] 補間
  '#8BBAE6', // [3] 補間
  '#63A1DC', // [4] 補間
  raundColors.info.base, // [5] #3B82C4 ← primaryShade
  '#2F6BA3', // [6] 補間
  '#245583', // [7] 補間
  '#193E62', // [8] 補間
  '#0E2741', // [9] 最暗
]

const warningScale: MantineColorsTuple = [
  raundColors.warning.tint, // [0] #FEF7EC
  raundColors.warning.light, // [1] #FCEFD8
  '#F8D9A3', // [2] 補間
  '#F3C46E', // [3] 補間
  '#ECAF53', // [4] 補間
  raundColors.warning.base, // [5] #E6A23C ← primaryShade
  '#C98A2E', // [6] 補間
  '#AC7224', // [7] 補間
  '#8F5B1A', // [8] 補間
  '#724510', // [9] 最暗
]

const dangerScale: MantineColorsTuple = [
  raundColors.danger.tint, // [0] #FBF1F1
  raundColors.danger.light, // [1] #F6E2E2
  '#EDB9B9', // [2] 補間
  '#E39090', // [3] 補間
  '#D97B7B', // [4] 補間
  raundColors.danger.base, // [5] #D46A6A ← primaryShade
  raundColors.danger.hover, // [6] #C55757
  raundColors.danger.active, // [7] #AD4343
  '#8A3636', // [8] 補間
  '#662828', // [9] 最暗
]

// ──────────────────────────────────────────────────────────
// Mantine テーマ
// ──────────────────────────────────────────────────────────
export const raundTheme = createTheme({
  primaryColor: 'primary',
  primaryShade: 5,
  // mantine-focus-auto（primary 固定色 #FFC500）を置き換え、
  // フォーカス色をキャンペーンカラー（--main-color）に統一する
  focusClassName: focusRingStyles.focusRing,
  fontFamily: "'Noto Sans JP', sans-serif",
  spacing: raundSpacing,
  components: {
    // Mantine の body は align-items 未指定（= stretch）で、ラベルの line-height
    // （--label-lh: 20px）が本アプリのボックスサイズ（24px）より小さいため、
    // ラベル文字がボックスに対して 2px 上寄りに描画される。上下中央で揃える。
    // テーマに置くことで、components/UI 経由・Mantine 直接利用のどちらにも一度で効く。
    Checkbox: Checkbox.extend({ styles: { body: { alignItems: 'center' } } }),
    Radio: Radio.extend({ styles: { body: { alignItems: 'center' } } }),
  },
  colors: {
    primary: primaryScale,
    secondary: secondaryScale,
    tertiary: tertiaryScale,
    neutral: neutralScale,
    system: neutralScale,
    success: successScale,
    info: infoScale,
    warning: warningScale,
    danger: dangerScale,
  },
})
